
import React, { useState, useMemo, useCallback } from 'react';
import { useAppContext } from '../contexts/AppContext';
import {
    ChevronDown, ChevronRight, Box, Layers, History,
    List, GitBranch, Plus, Lock, Unlock, Edit2, Save, X,
    Download
} from 'lucide-react';
import { PbsRevisionLevel, PbsItemRevision, PbsRevisionStage, BomItem } from '../types';
import { REVISION_STAGE_LABELS } from '../types/pbsRevision';

// =============================================================================
// CONSTANTS
// =============================================================================

const STATUS_OPTIONS = [
    { value: 'not_designed', label: 'NOT DESIGNED' },
    { value: 'design_in_progress', label: 'DESIGN IN PROGRESS' },
    { value: 'designed', label: 'DESIGNED' },
    { value: 'ready_to_order', label: 'READY TO ORDER' },
    { value: 'ordered', label: 'ORDERED' },
    { value: 'received', label: 'RECEIVED' },
    { value: 'assembled', label: 'ASSEMBLED' },
    { value: 'deprecated', label: 'DEPRECATED' },
];

const TYPE_OPTIONS = ['ASSEMBLY', 'DESIGNED PART', 'PURCHASED PART', 'STANDARD PART'];
const BUILD_OPTIONS = ['MANUAL', 'SMT', 'CNC', 'MOLDED', 'ASSEMBLY', 'PURCHASED', 'OUTSOURCED'];

const STATUS_COLORS: Record<string, { bg: string; text: string; fill: string; label: string }> = {
    'assembled': { bg: 'bg-green-100', text: 'text-green-700', fill: '#22c55e', label: 'ASSEMBLED' },
    'design_in_progress': { bg: 'bg-blue-100', text: 'text-blue-700', fill: '#3b82f6', label: 'DESIGN IN PROGRESS' },
    'designed': { bg: 'bg-purple-100', text: 'text-purple-700', fill: '#a855f7', label: 'DESIGNED' },
    'not_designed': { bg: 'bg-gray-100', text: 'text-gray-500', fill: '#9ca3af', label: 'NOT DESIGNED' },
    'ordered': { bg: 'bg-orange-100', text: 'text-orange-700', fill: '#f97316', label: 'ORDERED' },
    'ready_to_order': { bg: 'bg-yellow-100', text: 'text-yellow-700', fill: '#eab308', label: 'READY TO ORDER' },
    'received': { bg: 'bg-teal-100', text: 'text-teal-700', fill: '#14b8a6', label: 'RECEIVED' },
    'deprecated': { bg: 'bg-red-100', text: 'text-red-700', fill: '#ef4444', label: 'DEPRECATED' },
    'active': { bg: 'bg-blue-100', text: 'text-blue-700', fill: '#3b82f6', label: 'ACTIVE' },
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

const normalizeStatus = (status: string): string =>
    status?.toLowerCase().replace(/\s+/g, '_') || 'active';

const getStatusConfig = (status: string) =>
    STATUS_COLORS[normalizeStatus(status)] || STATUS_COLORS['active'];

const generateNextPbsNumber = (existingNumbers: string[], parentId: string | null): string => {
    if (!parentId) {
        const topLevels = existingNumbers
            .filter(n => n.endsWith('-000'))
            .map(n => parseInt(n.split('-')[0]) || 0);
        const maxTop = topLevels.length > 0 ? Math.max(...topLevels) : 0;
        const nextTop = maxTop === 0 ? 100 : maxTop + 100;
        return `${nextTop.toString().padStart(3, '0')}-000`;
    }
    const siblings = existingNumbers.filter(n => !n.endsWith('-000'));
    const nextSeq = siblings.length + 1;
    return `110-${nextSeq.toString().padStart(3, '0')}`;
};

const generateSequentialNumber = (index: number): string => {
    const group = Math.floor(index / 10) * 10 + 100;
    const seq = index % 10;
    return `${group.toString().padStart(3, '0')}-${seq.toString().padStart(3, '0')}`;
};

const getTypeClasses = (type: string): string => {
    if (type === 'ASSEMBLY') return 'bg-purple-100 text-purple-700';
    if (type === 'PURCHASED PART') return 'bg-orange-100 text-orange-700';
    return 'bg-blue-100 text-blue-700';
};

// =============================================================================
// SUB-COMPONENTS: Status & Charts
// =============================================================================

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const config = getStatusConfig(status);
    return (
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${config.bg} ${config.text} border-current/20`}>
            {config.label}
        </span>
    );
};

const StatusDropdown: React.FC<{ status: string; onChange: (s: string) => void }> = ({ status, onChange }) => (
    <select
        value={normalizeStatus(status)}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs px-2 py-1 rounded border border-border bg-card cursor-pointer hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
    >
        {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
    </select>
);

const StatusPieChart: React.FC<{ data: { status: string; count: number }[] }> = ({ data }) => {
    const total = data.reduce((sum, d) => sum + d.count, 0);
    if (total === 0) return null;

    let cumulativePercent = 0;
    const getCoords = (percent: number) => ({
        x: Math.cos(percent * 2 * Math.PI - Math.PI / 2) * 40 + 50,
        y: Math.sin(percent * 2 * Math.PI - Math.PI / 2) * 40 + 50
    });

    return (
        <div className="flex items-center gap-4">
            <svg viewBox="0 0 100 100" className="w-24 h-24">
                {data.map((item, idx) => {
                    const pct = item.count / total;
                    const start = getCoords(cumulativePercent);
                    cumulativePercent += pct;
                    const end = getCoords(cumulativePercent);
                    const color = getStatusConfig(item.status).fill;
                    if (pct === 1) return <circle key={idx} cx="50" cy="50" r="40" fill={color} />;
                    return (
                        <path key={idx}
                            d={`M 50 50 L ${start.x} ${start.y} A 40 40 0 ${pct > 0.5 ? 1 : 0} 1 ${end.x} ${end.y} Z`}
                            fill={color} />
                    );
                })}
                <circle cx="50" cy="50" r="25" fill="white" className="dark:fill-slate-800" />
            </svg>
            <ChartLegend data={data} />
        </div>
    );
};

const ChartLegend: React.FC<{ data: { status: string; count: number }[] }> = ({ data }) => (
    <div className="flex flex-col gap-1 text-xs">
        {data.filter(d => d.count > 0).map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: getStatusConfig(item.status).fill }} />
                <span className="text-text-secondary">{getStatusConfig(item.status).label}</span>
            </div>
        ))}
    </div>
);

const CompletionRing: React.FC<{ percent: number; label: string }> = ({ percent, label }) => {
    const r = 45, c = 2 * Math.PI * r;
    return (
        <div className="flex flex-col items-center">
            <svg viewBox="0 0 100 100" className="w-32 h-32">
                <circle cx="50" cy="50" r={r} fill="none" stroke="#e5e7eb" strokeWidth="6" className="dark:stroke-slate-700" />
                <circle cx="50" cy="50" r={r} fill="none" stroke="#22c55e" strokeWidth="6" strokeLinecap="round"
                    strokeDasharray={c} strokeDashoffset={c - (percent / 100) * c} transform="rotate(-90 50 50)" />
                <text x="50" y="50" textAnchor="middle" dominantBaseline="middle" className="text-2xl font-bold fill-text-primary">{percent}%</text>
            </svg>
            <p className="text-xs text-text-secondary mt-1">{label}</p>
        </div>
    );
};

// =============================================================================
// SUB-COMPONENTS: Form Fields
// =============================================================================

const FormInput: React.FC<{
    label: string; value: string; onChange: (v: string) => void;
    required?: boolean; placeholder?: string; type?: string; hint?: string; className?: string;
}> = ({ label, value, onChange, required, placeholder, type = 'text', hint, className = '' }) => (
    <div className={className}>
        <label className="block text-xs font-bold text-text-secondary uppercase mb-1">{label}{required && ' *'}</label>
        <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required}
            placeholder={placeholder} className="w-full p-2 rounded border border-border bg-card text-text-primary" />
        {hint && <p className="text-[10px] text-text-secondary mt-1">{hint}</p>}
    </div>
);

const FormSelect: React.FC<{
    label: string; value: string; onChange: (v: string) => void;
    options: { value: string; label: string }[] | string[]; emptyOption?: string; className?: string;
}> = ({ label, value, onChange, options, emptyOption, className = '' }) => (
    <div className={className}>
        <label className="block text-xs font-bold text-text-secondary uppercase mb-1">{label}</label>
        <select value={value} onChange={e => onChange(e.target.value)}
            className="w-full p-2 rounded border border-border bg-card text-text-primary">
            {emptyOption && <option value="">{emptyOption}</option>}
            {options.map(opt => typeof opt === 'string'
                ? <option key={opt} value={opt}>{opt}</option>
                : <option key={opt.value} value={opt.value}>{opt.label}</option>
            )}
        </select>
    </div>
);

// =============================================================================
// SUB-COMPONENTS: List Row & Tree Node
// =============================================================================

interface TreeItem extends PbsItemRevision { children?: TreeItem[] }

interface ListRowProps {
    item: TreeItem;
    level: number;
    isExpanded: boolean;
    onToggle: () => void;
    onEdit: () => void;
    onStatusChange: (status: string) => void;
}

const ListRow: React.FC<ListRowProps> = ({ item, level, isExpanded, onToggle, onEdit, onStatusChange }) => {
    const hasChildren = (item.children?.length || 0) > 0;
    const indent = level * 24;
    return (
        <>
            <tr className="border-b border-border hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors">
                <td className="py-3 pl-4 pr-2 font-mono text-sm text-primary font-medium">{item.pbsNumber}</td>
                <td className="py-3 px-2">
                    <div className="flex items-center" style={{ paddingLeft: `${indent}px` }}>
                        <button onClick={onToggle} className={`mr-2 p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-text-secondary ${!hasChildren ? 'invisible' : ''}`}>
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                        {item.type === 'ASSEMBLY' ? <Layers size={16} className="text-primary mr-2" /> : <Box size={16} className="text-emerald-500 mr-2" />}
                        <div className="flex flex-col">
                            <span className="text-sm font-medium text-text-primary">{item.name}</span>
                            {item.partNumber && <span className="text-[10px] text-text-secondary">{item.partNumber} {item.partRevision ? `Rev ${item.partRevision}` : ''}</span>}
                        </div>
                    </div>
                </td>
                <td className="py-3 px-2 text-center">
                    {item.frozen ? <Lock size={14} className="text-blue-600 mx-auto" /> : <Unlock size={14} className="text-slate-300 mx-auto" />}
                </td>
                <td className="py-3 px-2"><StatusDropdown status={item.status} onChange={onStatusChange} /></td>
                <td className="py-3 px-2 text-xs text-text-secondary">{item.owner || '-'}</td>
                <td className="py-3 px-2 text-center text-sm">{item.quantity}</td>
                <td className="py-3 px-2"><span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeClasses(item.type)}`}>{item.type}</span></td>
                <td className="py-3 px-2 text-xs text-text-secondary">{item.buildMethod || '-'}</td>
                <td className="py-3 px-2 text-xs text-text-secondary truncate max-w-[120px]">{item.comment || '-'}</td>
                <td className="py-3 px-2">
                    <button onClick={onEdit} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-text-secondary hover:text-primary"><Edit2 size={14} /></button>
                </td>
            </tr>
        </>
    );
};

interface TreeNodeProps {
    item: TreeItem;
    level: number;
    isExpanded: boolean;
    onToggle: () => void;
    onEdit: () => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({ item, level, isExpanded, onToggle, onEdit }) => {
    const hasChildren = (item.children?.length || 0) > 0;
    const indent = level * 32;
    return (
        <div className="flex items-center py-2 px-4 hover:bg-slate-50 dark:hover:bg-slate-900/50 border-b border-border/50 cursor-pointer"
            style={{ paddingLeft: `${indent + 16}px` }} onClick={onToggle}>
            <div className="flex items-center gap-2 mr-3">
                {level > 0 && <div className="w-4 h-px bg-border" />}
                {hasChildren ? <button onClick={e => { e.stopPropagation(); onToggle(); }} className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700">
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button> : <div className="w-5" />}
            </div>
            {item.type === 'ASSEMBLY' ? <Layers size={16} className="text-primary mr-2" /> : <Box size={16} className="text-emerald-500 mr-2" />}
            <span className="font-mono text-xs text-primary mr-3 min-w-[60px]">{item.pbsNumber}</span>
            <span className="text-sm font-medium text-text-primary flex-1 truncate">{item.name}</span>
            <div className="flex items-center gap-3 ml-auto">
                <StatusBadge status={item.status} />
                <span className="text-xs text-text-secondary min-w-[70px]">{item.owner || '-'}</span>
                <button onClick={e => { e.stopPropagation(); onEdit(); }} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-text-secondary hover:text-primary">
                    <Edit2 size={14} />
                </button>
            </div>
        </div>
    );
};

// =============================================================================
// SUB-COMPONENTS: Modals
// =============================================================================

interface ItemModalProps {
    item?: PbsItemRevision;
    existingItems: PbsItemRevision[];
    revisionId: string;
    projectName: string;
    onSave: (item: PbsItemRevision) => void;
    onClose: () => void;
    mode: 'add' | 'edit';
}

const ItemModal: React.FC<ItemModalProps> = ({ item, existingItems, revisionId, projectName, onSave, onClose, mode }) => {
    const defaultItem = (): Partial<PbsItemRevision> => ({
        id: `PBS-${Date.now().toString(36).toUpperCase()}`,
        pbsNumber: generateNextPbsNumber(existingItems.map(i => i.pbsNumber).filter(Boolean), null),
        name: '', type: 'DESIGNED PART' as any, revisionLevel: revisionId, partNumber: '', partRevision: 'A',
        quantity: 1, reviewStatus: 'pending', frozen: false, status: 'not_designed' as any, owner: '',
        projectName, epochId: 1
    });

    const [form, setForm] = useState<Partial<PbsItemRevision>>(item ? { ...item } : defaultItem());

    const handleParentChange = (parentId: string | null) => {
        const newNum = generateNextPbsNumber(existingItems.map(i => i.pbsNumber).filter(Boolean), parentId);
        setForm(prev => ({ ...prev, parentId: parentId || undefined, pbsNumber: newNum }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(form as PbsItemRevision);
        onClose();
    };

    const assemblies = existingItems.filter(i => i.type === 'ASSEMBLY');

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-[700px] max-h-[90vh] overflow-y-auto border border-border">
                <ModalHeader title={mode === 'add' ? 'Add New PBS Item' : 'Edit PBS Item'} icon={mode === 'add' ? Plus : Edit2} onClose={onClose} />
                <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-5">
                    <div className="grid grid-cols-3 gap-4">
                        <FormInput label="PBS Number" value={form.pbsNumber || ''} onChange={v => setForm({ ...form, pbsNumber: v })} required hint="e.g., 100-000, 110-001" className="font-mono" />
                        <FormInput label="Part Number" value={form.partNumber || ''} onChange={v => setForm({ ...form, partNumber: v })} placeholder="PN-XXXXX" />
                        <FormInput label="Revision" value={form.partRevision || ''} onChange={v => setForm({ ...form, partRevision: v })} placeholder="A" />
                    </div>
                    <FormInput label="Component Name" value={form.name || ''} onChange={v => setForm({ ...form, name: v })} required placeholder="e.g., Main Assembly, Drive Shaft" />
                    <FormSelect label="Parent Assembly" value={form.parentId || ''} onChange={v => handleParentChange(v || null)}
                        options={assemblies.map(a => ({ value: a.id, label: `${a.pbsNumber} - ${a.name}` }))} emptyOption="(Top Level - No Parent)" />
                    <div className="grid grid-cols-2 gap-4">
                        <FormSelect label="Type" value={form.type || 'DESIGNED PART'} onChange={v => setForm({ ...form, type: v as any })} options={TYPE_OPTIONS} />
                        <FormSelect label="Status" value={normalizeStatus(form.status || 'not_designed')} onChange={v => setForm({ ...form, status: v as any })} options={STATUS_OPTIONS} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <FormInput label="Owner" value={form.owner || ''} onChange={v => setForm({ ...form, owner: v })} placeholder="Engineer name" />
                        <FormSelect label="Build Method" value={form.buildMethod || ''} onChange={v => setForm({ ...form, buildMethod: v })} options={BUILD_OPTIONS} emptyOption="Select..." />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <FormInput label="Quantity" value={String(form.quantity || 1)} onChange={v => setForm({ ...form, quantity: parseInt(v) || 1 })} type="number" />
                        <FormInput label="Supplier" value={(form as any).supplier || ''} onChange={v => setForm({ ...form, supplier: v } as any)} />
                        <div className="flex items-end pb-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={form.frozen || false} onChange={e => setForm({ ...form, frozen: e.target.checked })} className="w-4 h-4 rounded" />
                                <span className="text-sm font-medium flex items-center gap-1">
                                    {form.frozen ? <Lock size={14} className="text-blue-600" /> : <Unlock size={14} className="text-gray-400" />} Frozen
                                </span>
                            </label>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-text-secondary uppercase mb-1">Comment / Notes</label>
                        <textarea value={form.comment || ''} onChange={e => setForm({ ...form, comment: e.target.value })}
                            className="w-full p-2 rounded border border-border bg-card text-text-primary resize-none h-20" placeholder="Additional notes..." />
                    </div>
                    <ModalFooter onClose={onClose} submitLabel={mode === 'add' ? 'Add Component' : 'Save Changes'} />
                </form>
            </div>
        </div>
    );
};

interface ImportBomModalProps {
    bomItems: BomItem[];
    existingPbsItems: PbsItemRevision[];
    revisionId: string;
    projectName: string;
    onImport: (items: PbsItemRevision[]) => void;
    onClose: () => void;
}

const ImportBomModal: React.FC<ImportBomModalProps> = ({ bomItems, existingPbsItems, revisionId, projectName, onImport, onClose }) => {
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const importedBomIds = new Set(existingPbsItems.map(p => p.bomItemId).filter(Boolean));
    const availableItems = bomItems.filter(b => !importedBomIds.has(b.id));

    const toggleSelect = (id: number) => setSelectedIds(prev => {
        const n = new Set(prev);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });

    const handleImport = () => {
        let seq = existingPbsItems.length;
        const newItems: PbsItemRevision[] = availableItems
            .filter(b => selectedIds.has(b.id))
            .map((bom, idx) => ({
                id: `PBS-${Date.now().toString(36).toUpperCase()}-${idx}`,
                pbsNumber: generateSequentialNumber(seq + idx),
                name: bom.component,
                type: 'DESIGNED PART' as any,
                revisionLevel: revisionId, partNumber: bom.partNumber || '', partRevision: 'A',
                quantity: bom.quantity || 1, reviewStatus: 'pending' as const, frozen: false, status: 'not_designed' as any,
                owner: '', buildMethod: '', comment: bom.description || '', projectName, epochId: 1, bomItemId: bom.id,
                parentId: bom.parentId ? existingPbsItems.find(p => p.bomItemId === bom.parentId)?.id : undefined
            }));
        onImport(newItems);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-[800px] max-h-[80vh] overflow-hidden border border-border flex flex-col">
                <div className="p-4 border-b border-border flex justify-between items-center bg-gradient-to-r from-emerald-600 to-teal-600">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2"><Download size={20} /> Import from Master BOM</h3>
                    <button onClick={onClose} className="text-white/80 hover:text-white p-1"><X size={20} /></button>
                </div>
                <div className="p-4 flex-1 overflow-auto">
                    {availableItems.length === 0 ? (
                        <EmptyState icon={Box} title="All BOM items imported" subtitle="Add more items to System Definition (BOM) first." />
                    ) : (
                        <BomImportTable items={availableItems} selectedIds={selectedIds} onToggle={toggleSelect}
                            onSelectAll={() => setSelectedIds(selectedIds.size === availableItems.length ? new Set() : new Set(availableItems.map(b => b.id)))} />
                    )}
                </div>
                <div className="p-4 border-t border-border flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
                    <p className="text-sm text-text-secondary">{selectedIds.size} items selected</p>
                    <div className="flex gap-3">
                        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
                        <button onClick={handleImport} disabled={selectedIds.size === 0}
                            className="px-4 py-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg flex items-center gap-2 disabled:opacity-50">
                            <Download size={16} /> Import {selectedIds.size} Items
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const BomImportTable: React.FC<{ items: BomItem[]; selectedIds: Set<number>; onToggle: (id: number) => void; onSelectAll: () => void }> = ({ items, selectedIds, onToggle, onSelectAll }) => (
    <>
        <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-text-secondary">{items.length} items available</p>
            <button onClick={onSelectAll} className="text-sm text-primary hover:underline">
                {selectedIds.size === items.length ? 'Deselect All' : 'Select All'}
            </button>
        </div>
        <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-xs font-bold text-text-secondary uppercase">
                <tr>
                    <th className="py-2 px-3 border-b border-border w-8"></th>
                    <th className="py-2 px-3 border-b border-border">ID</th>
                    <th className="py-2 px-3 border-b border-border">Component</th>
                    <th className="py-2 px-3 border-b border-border">Part #</th>
                    <th className="py-2 px-3 border-b border-border">Type</th>
                    <th className="py-2 px-3 border-b border-border text-center">Qty</th>
                </tr>
            </thead>
            <tbody>
                {items.map(bom => (
                    <tr key={bom.id} className="border-b border-border hover:bg-slate-50 dark:hover:bg-slate-900/30 cursor-pointer" onClick={() => onToggle(bom.id)}>
                        <td className="py-2 px-3"><input type="checkbox" checked={selectedIds.has(bom.id)} onChange={() => onToggle(bom.id)} className="w-4 h-4 rounded" /></td>
                        <td className="py-2 px-3 font-mono text-sm text-primary">{bom.id}</td>
                        <td className="py-2 px-3 font-medium">{bom.component}</td>
                        <td className="py-2 px-3 text-sm text-text-secondary">{bom.partNumber || '-'}</td>
                        <td className="py-2 px-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeClasses('PART')}`}>PART</span></td>
                        <td className="py-2 px-3 text-center">{bom.quantity || 1}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </>
);

// =============================================================================
// SUB-COMPONENTS: Shared UI
// =============================================================================

const ModalHeader: React.FC<{ title: string; icon: any; onClose: () => void }> = ({ title, icon: Icon, onClose }) => (
    <div className="p-4 border-b border-border flex justify-between items-center bg-gradient-to-r from-primary to-primary-dark sticky top-0">
        <h3 className="text-lg font-bold text-white flex items-center gap-2"><Icon size={20} />{title}</h3>
        <button onClick={onClose} className="text-white/80 hover:text-white p-1"><X size={20} /></button>
    </div>
);

const ModalFooter: React.FC<{ onClose: () => void; submitLabel: string }> = ({ onClose, submitLabel }) => (
    <div className="flex gap-3 justify-end mt-4 pt-4 border-t border-border">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
        <button type="submit" className="px-4 py-2 text-sm font-bold text-white bg-primary hover:bg-primary-dark rounded-lg shadow-lg flex items-center gap-2">
            <Save size={16} /> {submitLabel}
        </button>
    </div>
);

const EmptyState: React.FC<{ icon: any; title: string; subtitle: string; actions?: React.ReactNode }> = ({ icon: Icon, title, subtitle, actions }) => (
    <div className="flex flex-col items-center justify-center h-full text-text-secondary py-12">
        <Icon size={48} className="mb-4 text-slate-300" />
        <p className="text-lg font-medium">{title}</p>
        <p className="text-sm mb-4">{subtitle}</p>
        {actions}
    </div>
);

// =============================================================================
// SUB-COMPONENTS: Header & Stats
// =============================================================================

interface HeaderProps {
    activeProject: string;
    selectedRevisionId: string | null;
    projectRevisions: PbsRevisionLevel[];
    viewMode: 'list' | 'tree';
    onRevisionChange: (id: string) => void;
    onViewModeChange: (mode: 'list' | 'tree') => void;
    onAddRevision: () => void;
    onImport: () => void;
    onAddItem: () => void;
}

const Header: React.FC<HeaderProps> = ({ activeProject, selectedRevisionId, projectRevisions, viewMode, onRevisionChange, onViewModeChange, onAddRevision, onImport, onAddItem }) => (
    <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
            <div className="bg-primary p-2 rounded-lg shadow-sm"><Layers className="text-white" size={24} /></div>
            <div>
                <h1 className="text-2xl font-bold text-text-primary">PBS Manager Pro</h1>
                <p className="text-xs text-text-secondary">Product Breakdown Structure System</p>
            </div>
            <div className="ml-8 border-l border-border pl-8 flex items-center gap-4">
                <div>
                    <label className="block text-[10px] font-bold text-text-secondary uppercase mb-1">Project</label>
                    <div className="font-medium text-sm text-text-primary">{activeProject === 'all' ? 'All Projects' : activeProject}</div>
                </div>
                <div>
                    <label className="block text-[10px] font-bold text-text-secondary uppercase mb-1">Revision Level</label>
                    <div className="flex items-center gap-2">
                        <select className="bg-white dark:bg-slate-800 border border-border rounded px-2 py-1 text-sm min-w-[150px]"
                            value={selectedRevisionId || ''} onChange={e => onRevisionChange(e.target.value)}>
                            <option value="" disabled>Select Revision...</option>
                            {projectRevisions.map(rev => (
                                <option key={rev.id} value={rev.id}>{rev.name} ({REVISION_STAGE_LABELS[rev.stage] || rev.stage}) {rev.isActive ? '★' : ''}</option>
                            ))}
                        </select>
                        <button onClick={onAddRevision} disabled={activeProject === 'all'}
                            className="p-1 rounded border border-border hover:bg-slate-100 dark:hover:bg-slate-700 text-text-secondary disabled:opacity-50" title="Create New Revision">
                            <Plus size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div className="flex gap-3">
            <div className="flex border border-border rounded-lg overflow-hidden">
                <button onClick={() => onViewModeChange('list')} className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-slate-900 dark:bg-slate-700 text-white' : 'bg-white dark:bg-slate-800 text-text-secondary hover:bg-slate-50'}`}>
                    <List size={16} /> List View
                </button>
                <button onClick={() => onViewModeChange('tree')} className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${viewMode === 'tree' ? 'bg-slate-900 dark:bg-slate-700 text-white' : 'bg-white dark:bg-slate-800 text-text-secondary hover:bg-slate-50'}`}>
                    <GitBranch size={16} /> Tree View
                </button>
            </div>
            <button onClick={onImport} disabled={!selectedRevisionId}
                className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-50">
                <Download size={16} /> Import BOM
            </button>
            <button onClick={onAddItem} disabled={!selectedRevisionId}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors shadow-sm disabled:opacity-50">
                <Plus size={16} /> Add Component
            </button>
        </div>
    </div>
);

const StatsRow: React.FC<{ stats: { completionPercent: number; assembled: number; total: number; statusData: { status: string; count: number }[] } }> = ({ stats }) => (
    <div className="grid grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-xl p-6 flex items-center gap-8">
            <div className="text-center">
                <p className="text-xs font-bold text-text-secondary uppercase mb-2">Project Completion</p>
                <CompletionRing percent={stats.completionPercent} label={`${stats.assembled} / ${stats.total} Items Assembled`} />
            </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-6">
            <p className="text-xs font-bold text-text-secondary uppercase mb-4">Status Breakdown</p>
            <StatusPieChart data={stats.statusData} />
        </div>
    </div>
);

// =============================================================================
// MAIN COMPONENT (Complexity < 15)
// =============================================================================

const PbsManager: React.FC = () => {
    const { allData, activeProject, createPbsRevision, updatePbsItem } = useAppContext();

    // State
    const [selectedRevisionId, setSelectedRevisionId] = useState<string | null>(null);
    const [isRevisionModalOpen, setRevisionModalOpen] = useState(false);
    const [isItemModalOpen, setItemModalOpen] = useState(false);
    const [isImportModalOpen, setImportModalOpen] = useState(false);
    const [viewMode, setViewMode] = useState<'list' | 'tree'>('list');
    const [editingItem, setEditingItem] = useState<PbsItemRevision | null>(null);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [revisionForm, setRevisionForm] = useState({ name: '', stage: 'alpha-1' as PbsRevisionStage, desc: '', baseId: '' });

    // Derived Data
    const projectRevisions = useMemo(() => (allData.pbsRevisions || [])
        .filter(r => activeProject === 'all' || r.projectName === activeProject)
        .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()), [allData.pbsRevisions, activeProject]);

    const currentItems = useMemo(() => selectedRevisionId ? (allData.pbsItems || []).filter(i => i.revisionLevel === selectedRevisionId) : [], [allData.pbsItems, selectedRevisionId]);

    const itemTree = useMemo(() => {
        const map = new Map<string, TreeItem>();
        const roots: TreeItem[] = [];
        currentItems.forEach(item => map.set(item.id, { ...item, children: [] }));
        currentItems.forEach(item => {
            const node = map.get(item.id)!;
            if (item.parentId && map.has(item.parentId)) map.get(item.parentId)!.children!.push(node);
            else roots.push(node);
        });
        roots.sort((a, b) => (a.pbsNumber || '').localeCompare(b.pbsNumber || ''));
        return roots;
    }, [currentItems]);

    const stats = useMemo(() => {
        const counts: Record<string, number> = {};
        let assembled = 0;
        currentItems.forEach(item => {
            counts[item.status] = (counts[item.status] || 0) + 1;
            if (normalizeStatus(item.status) === 'assembled') assembled++;
        });
        return {
            statusData: Object.entries(counts).map(([status, count]) => ({ status, count })),
            completionPercent: currentItems.length > 0 ? Math.round((assembled / currentItems.length) * 100) : 0,
            total: currentItems.length, assembled
        };
    }, [currentItems]);

    // Auto-select first revision
    if (!selectedRevisionId && projectRevisions.length > 0) {
        const active = projectRevisions.find(r => r.isActive);
        setSelectedRevisionId(active ? active.id : projectRevisions[0].id);
    }

    // Handlers
    const toggleExpand = useCallback((id: string) => setExpandedIds(prev => {
        const n = new Set(prev);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    }), []);

    const handleStatusChange = useCallback(async (item: PbsItemRevision, status: string) => {
        await updatePbsItem({ ...item, status: status as any });
    }, [updatePbsItem]);

    const handleSaveItem = useCallback(async (item: PbsItemRevision) => {
        await updatePbsItem(item);
        setEditingItem(null);
        setItemModalOpen(false);
    }, [updatePbsItem]);

    const handleImportItems = useCallback(async (items: PbsItemRevision[]) => {
        for (const item of items) await updatePbsItem(item);
    }, [updatePbsItem]);

    const handleCreateRevision = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!activeProject || activeProject === 'all') return alert("Select a project first.");
        const newRev: PbsRevisionLevel = {
            id: `PBS-REV-${Date.now().toString(36).toUpperCase()}`, name: revisionForm.name, stage: revisionForm.stage,
            createdDate: new Date().toISOString(), createdBy: 'YOU', description: revisionForm.desc,
            carriedOverFrom: revisionForm.baseId || undefined, isActive: true, status: 'planning', projectName: activeProject, itemCount: 0
        };
        await createPbsRevision(newRev);
        setSelectedRevisionId(newRev.id);
        setRevisionModalOpen(false);
        setRevisionForm({ name: '', stage: 'alpha-1', desc: '', baseId: '' });
    }, [activeProject, revisionForm, createPbsRevision]);

    const renderRows = useCallback((items: TreeItem[], level = 0): React.ReactNode => items.map(item => (
        <React.Fragment key={item.id}>
            <ListRow item={item} level={level} isExpanded={expandedIds.has(item.id)} onToggle={() => toggleExpand(item.id)}
                onEdit={() => { setEditingItem(item); setItemModalOpen(true); }} onStatusChange={s => handleStatusChange(item, s)} />
            {expandedIds.has(item.id) && item.children && renderRows(item.children, level + 1)}
        </React.Fragment>
    )), [expandedIds, toggleExpand, handleStatusChange]);

    const renderTree = useCallback((items: TreeItem[], level = 0): React.ReactNode => items.map(item => (
        <React.Fragment key={item.id}>
            <TreeNode item={item} level={level} isExpanded={expandedIds.has(item.id)} onToggle={() => toggleExpand(item.id)}
                onEdit={() => { setEditingItem(item); setItemModalOpen(true); }} />
            {expandedIds.has(item.id) && item.children && (
                <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-px bg-border" style={{ left: `${level * 32 + 26}px` }} />
                    {renderTree(item.children, level + 1)}
                </div>
            )}
        </React.Fragment>
    )), [expandedIds, toggleExpand]);

    return (
        <div className="flex flex-col gap-6 h-full font-sans p-6">
            <Header activeProject={activeProject} selectedRevisionId={selectedRevisionId} projectRevisions={projectRevisions}
                viewMode={viewMode} onRevisionChange={setSelectedRevisionId} onViewModeChange={setViewMode}
                onAddRevision={() => setRevisionModalOpen(true)} onImport={() => setImportModalOpen(true)}
                onAddItem={() => { setEditingItem(null); setItemModalOpen(true); }} />

            {viewMode === 'list' && currentItems.length > 0 && <StatsRow stats={stats} />}

            <div className="bg-card border border-border rounded-xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <div className="p-4 border-b border-border flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
                    <div className="px-4 py-2 bg-white dark:bg-slate-800 rounded border border-border flex items-center gap-2">
                        <span className="text-2xl font-bold text-primary">{currentItems.length}</span>
                        <span className="text-xs text-text-secondary uppercase font-bold">Total Items</span>
                    </div>
                    <div className="text-xs text-text-secondary">{viewMode === 'list' ? 'Breakdown Structure' : 'Tree Visualization'}</div>
                </div>

                <div className="flex-1 overflow-auto">
                    {!selectedRevisionId ? (
                        <EmptyState icon={History} title="No Revision Selected" subtitle="Create a revision level to start adding components."
                            actions={<button onClick={() => setRevisionModalOpen(true)} disabled={activeProject === 'all'}
                                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50">
                                <Plus size={16} /> Create Revision
                            </button>} />
                    ) : currentItems.length === 0 ? (
                        <EmptyState icon={Box} title="No PBS Items Found" subtitle="Add components manually or import from the Master BOM."
                            actions={<div className="flex gap-3">
                                <button onClick={() => setImportModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
                                    <Download size={16} /> Import from BOM
                                </button>
                                <button onClick={() => { setEditingItem(null); setItemModalOpen(true); }} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark">
                                    <Plus size={16} /> Add Manually
                                </button>
                            </div>} />
                    ) : viewMode === 'list' ? (
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50 dark:bg-slate-900/50 sticky top-0 z-10 text-xs font-bold text-text-secondary uppercase tracking-wider">
                                <tr>
                                    <th className="py-3 text-left pl-4 pr-2 border-b border-border">PBS #</th>
                                    <th className="py-3 text-left px-2 border-b border-border w-1/4">Name</th>
                                    <th className="py-3 text-center px-2 border-b border-border">Frozen</th>
                                    <th className="py-3 text-left px-2 border-b border-border">Status</th>
                                    <th className="py-3 text-left px-2 border-b border-border">Owner</th>
                                    <th className="py-3 text-center px-2 border-b border-border">Qty</th>
                                    <th className="py-3 text-left px-2 border-b border-border">Type</th>
                                    <th className="py-3 text-left px-2 border-b border-border">Build</th>
                                    <th className="py-3 text-left px-2 border-b border-border">Comment</th>
                                    <th className="py-3 text-center px-2 border-b border-border w-12"></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-card">{renderRows(itemTree)}</tbody>
                        </table>
                    ) : <div className="py-2">{renderTree(itemTree)}</div>}
                </div>
            </div>

            {isItemModalOpen && selectedRevisionId && (
                <ItemModal item={editingItem || undefined} existingItems={currentItems} revisionId={selectedRevisionId}
                    projectName={activeProject} onSave={handleSaveItem} onClose={() => { setItemModalOpen(false); setEditingItem(null); }} mode={editingItem ? 'edit' : 'add'} />
            )}

            {isImportModalOpen && selectedRevisionId && (
                <ImportBomModal bomItems={allData.bomItems || []} existingPbsItems={currentItems} revisionId={selectedRevisionId}
                    projectName={activeProject} onImport={handleImportItems} onClose={() => setImportModalOpen(false)} />
            )}

            {isRevisionModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-[500px] border border-border overflow-hidden">
                        <ModalHeader title="New Revision Level" icon={History} onClose={() => setRevisionModalOpen(false)} />
                        <form onSubmit={handleCreateRevision} className="p-6 flex flex-col gap-4">
                            <div className="p-2 bg-slate-100 dark:bg-slate-900 rounded border border-border text-sm font-mono text-text-primary">{activeProject}</div>
                            <FormInput label="Revision Name" value={revisionForm.name} onChange={v => setRevisionForm({ ...revisionForm, name: v })} required placeholder="e.g., Alpha 2, Beta 1" />
                            <div className="grid grid-cols-2 gap-4">
                                <FormSelect label="Maturity Stage" value={revisionForm.stage} onChange={v => setRevisionForm({ ...revisionForm, stage: v as PbsRevisionStage })}
                                    options={Object.entries(REVISION_STAGE_LABELS).map(([k, v]) => ({ value: k, label: v }))} />
                                <FormSelect label="Based On" value={revisionForm.baseId} onChange={v => setRevisionForm({ ...revisionForm, baseId: v })}
                                    options={projectRevisions.map(r => ({ value: r.id, label: `${r.name} (${r.stage})` }))} emptyOption="(None - Clean Start)" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-text-secondary uppercase mb-1">Description</label>
                                <textarea className="w-full p-2 rounded border border-border bg-card text-text-primary h-24 resize-none"
                                    value={revisionForm.desc} onChange={e => setRevisionForm({ ...revisionForm, desc: e.target.value })} placeholder="Purpose of this revision..." />
                            </div>
                            <ModalFooter onClose={() => setRevisionModalOpen(false)} submitLabel="Create Revision" />
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PbsManager;
